import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { featureFlagGuard, permissionGuard } from './guards'
import { adminRoutes } from './admin'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import Page404Layout from '../pages/404.vue'

const routes: Array<RouteRecordRaw> = [
  {
    name: 'admin',
    path: '/org/:orgId',
    component: AppLayout,
    redirect: () => ({ name: 'dashboard' }),
    props: true,
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'faq',
        path: 'faq',
        component: () => import('../pages/faq/FaqPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'projects',
        path: 'projects',
        component: () => import('../pages/projects/ProjectsPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'project-detail',
        path: 'projects/:id',
        component: () => import('../pages/projects/ProjectDetailPage.vue'),
        props: true,
        redirect: { name: 'project-forms' },
        meta: {
          requiresAuth: true,
          breadcrumb: [
            { title: 'Projects', path: '/org/:orgId/projects' },
            { title: 'Project', path: '' },
          ],
        },
        children: [
          {
            name: 'project-forms',
            path: '',
            component: () => import('../pages/projects/sections/ProjectFormsPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'project-users',
            path: 'users',
            component: () => import('../pages/projects/sections/ProjectUsersPage.vue'),
            meta: {
              requiresAuth: true,
              breadcrumb: [
                { title: 'Projects', path: '/org/:orgId/projects' },
                { title: 'Project', path: '/org/:orgId/projects/:id' },
                { title: 'Users', path: '' },
              ],
            },
          },
          {
            name: 'project-qr-code',
            path: 'qr-code',
            component: () => import('../pages/projects/sections/ProjectQrCodePage.vue'),
            meta: {
              requiresAuth: true,
              breadcrumb: [
                { title: 'Projects', path: '/org/:orgId/projects' },
                { title: 'Project', path: '/org/:orgId/projects/:id' },
                { title: 'QR Code', path: '' },
              ],
            },
          },
          {
            name: 'project-form-access',
            path: 'form-access',
            component: () => import('../pages/projects/sections/ProjectFormAccessPage.vue'),
            meta: {
              requiresAuth: true,
              breadcrumb: [
                { title: 'Projects', path: '/org/:orgId/projects' },
                { title: 'Project', path: '/org/:orgId/projects/:id' },
                { title: 'Form Access', path: '' },
              ],
            },
          },
          {
            name: 'project-cleanup',
            path: 'cleanup',
            component: () => import('../pages/odk/OdkCleanupPage.vue'),
            meta: {
              requiresAuth: true,
              breadcrumb: [
                { title: 'Projects', path: '/org/:orgId/projects' },
                { title: 'Project', path: '/org/:orgId/projects/:id' },
                { title: 'ODK Cleanup', path: '' },
              ],
            },
          },
          {
            name: 'project-info',
            path: 'info',
            component: () => import('../pages/projects/sections/ProjectInfoPage.vue'),
            meta: {
              requiresAuth: true,
              breadcrumb: [
                { title: 'Projects', path: '/org/:orgId/projects' },
                { title: 'Project', path: '/org/:orgId/projects/:id' },
                { title: 'Info', path: '' },
              ],
            },
          },
          {
            name: 'project-map',
            path: 'map',
            component: () => import('../pages/projects/sections/map/ProjectMapPage.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              breadcrumb: [
                { title: 'Projects', path: '/org/:orgId/projects' },
                { title: 'Project', path: '/org/:orgId/projects/:id' },
                { title: 'Map', path: '' },
              ],
            },
          },
        ],
      },
      {
        name: 'odk-servers',
        path: 'odk-servers',
        component: () => import('../pages/odk_servers/OdkServersPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'odk-sync',
        path: 'odk-sync',
        component: () => import('../pages/odk_sync/OdkSyncDashboard.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'my-organization',
        path: 'organization',
        component: () => import('../layouts/OrganizationLayout.vue'),
        meta: { requiresAuth: true, disallowClients: true },
        redirect: { name: 'user-management' },
        children: [
          {
            name: 'user-management',
            path: 'user-management',
            component: () => import('../pages/users/UsersPage.vue'),
            meta: { requiresAuth: true, disallowClients: true },
          },
          {
            name: 'organization-stats',
            path: 'stats',
            component: () => import('../pages/organizations/OrganizationStats.vue'),
            meta: { requiresAuth: true, disallowClients: true },
          },
          {
            name: 'organization-settings',
            path: 'settings',
            component: () => import('../pages/organizations/OrganizationSettings.vue'),
            meta: { requiresAuth: true, disallowClients: true },
          },
          {
            name: 'organization-report-templates',
            path: 'report-templates',
            component: () => import('../pages/organizations/ReportTemplatesAdmin.vue'),
            meta: { requiresAuth: true, disallowClients: true },
          },
        ],
      },
      {
        name: 'organization-odk-import-projects',
        path: 'odk/import-projects',
        component: () => import('../pages/projects/import/ImportProjects.vue'),
        meta: {
          requiresAuth: true,
          layout: 'default',
          breadcrumb: [
            { title: 'Organization', path: '/org/:orgId' },
            { title: 'Import ODK Projects', path: '' },
          ],
        },
      },
      {
        name: 'organization-odk-import-forms',
        path: 'projects/:projectId/import-forms',
        component: () => import('../pages/projects/import/ImportForms.vue'),
        meta: {
          requiresAuth: true,
          layout: 'default',
          breadcrumb: [
            { title: 'Projects', path: '/org/:orgId/projects' },
            { title: 'Project', path: '/org/:orgId/projects/:projectId' },
            { title: 'Import Forms', path: '' },
          ],
        },
      },
      {
        name: 'xlsform-assist',
        path: 'projects/:projectId/xlsform-assist',
        component: () => import('../pages/projects/xlsform/XlsformAssistPage.vue'),
        props: (route) => ({
          orgId: Number(route.params.orgId),
          projectId: Number(route.params.projectId),
          initialSessionId: (route.query.session as string) || undefined,
        }),
        meta: {
          requiresAuth: true,
          immersiveLayout: true,
          breadcrumb: [
            { title: 'Projects', path: '/org/:orgId/projects' },
            { title: 'Project', path: '/org/:orgId/projects/:projectId' },
            { title: 'AI XLSForm', path: '' },
          ],
        },
      },
      {
        name: 'form-detail',
        path: 'projects/:projectId/forms/:formId',
        component: () => import('../pages/projects/forms/FormDetailPage.vue'),
        props: true,
        redirect: { name: 'form-dashboard' },
        meta: {
          requiresAuth: true,
          breadcrumb: [
            { title: 'Projects', path: '/org/:orgId/projects' },
            { title: 'Project', path: '/org/:orgId/projects/:projectId' },
            { title: 'Form', path: '' },
          ],
        },
        children: [
          {
            name: 'form-dashboard',
            path: '',
            component: () => import('../pages/projects/forms/dashboard/FormDashboard.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'form-auto-reports',
            path: 'auto-reports',
            component: () => import('../pages/projects/forms/components/autoReports/FormAutoReports.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'form-qc',
            path: 'qc',
            component: () => import('../pages/projects/forms/components/qc/FormQcDashboard.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'form-qc-create-rule',
            path: 'qc/create-rule',
            component: () => import('../pages/projects/forms/components/qc/QcRuleCreator.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'form-qc-edit-rule',
            path: 'qc/edit-rule/:ruleId',
            component: () => import('../pages/projects/forms/components/qc/QcRuleCreator.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'form-data-export',
            path: 'data-export',
            component: () => import('../pages/projects/forms/components/FormDataExport.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'form-data-analysis',
            path: 'data-analysis',
            component: () => import('../pages/projects/forms/components/FormDataAnalysis.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'form-data-monitoring',
            path: 'data-monitoring',
            component: () => import('../pages/projects/forms/components/FormDataMonitoring.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'form-settings',
            path: 'settings',
            component: () => import('../pages/projects/forms/components/FormSettings.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'form-datasets',
            path: 'datasets',
            component: () => import('../pages/projects/forms/components/FormDatasets.vue'),
            meta: { requiresAuth: true },
          },
        ],
      },
    ],
  },
  {
    name: 'root-redirect',
    path: '/',
    redirect: (_to) => {
      // Redirect to organization dashboard if user has organization
      const authStore = useAuthStore()
      if (authStore.isAuthenticated && authStore.organizationId) {
        console.log('[Root Redirect] Redirecting to org dashboard:', authStore.organizationId)
        return { name: 'admin', params: { orgId: authStore.organizationId } }
      }
      console.log('[Root Redirect] Redirecting to login')
      return { name: 'login' }
    },
  },
  {
    name: 'org-redirect',
    path: '/org/:orgId',
    redirect: (to) => {
      // Redirect to organization dashboard
      return { name: 'admin', params: { orgId: to.params.orgId } }
    },
  },
  // TODO remove this part after fixing
  {
    name: 'organization-redirect',
    path: '/organizations/:orgId',
    redirect: (to) => {
      // Redirect old organization URLs to new org URLs
      return { name: 'admin', params: { orgId: to.params.orgId } }
    },
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        name: 'reset-password',
        path: 'reset-password', // URL: /auth/reset-password?token=...
        component: () => import('../pages/auth/ResetPassword.vue'),
        props: (route) => ({ token: route.query.token }), // Передаем токен как пропс
      },
      {
        name: 'create-organization',
        path: 'create-organization',
        component: () => import('../pages/auth/CreateOrganization.vue'),
        meta: { requiresAuth: false }, // Don't require auth check to prevent circular redirect
      },
      {
        name: 'verify-2fa',
        path: 'verify-2fa',
        component: () => import('../pages/auth/Verify2FA.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: 'share-briefing',
    path: '/share/:token',
    component: () => import('../pages/share/ShareBriefingPage.vue'),
    meta: { requiresAuth: false, publicShare: true },
  },
  // Admin routes (must be registered before the catch-all 404 route)
  ...adminRoutes,
  {
    path: '/404',
    component: Page404Layout,
    children: [
      {
        name: 'not-found-page', // Changed name to avoid conflict
        path: '',
        component: () => import('../pages/404.vue'),
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'catch-all-404', // Changed name to avoid conflict
    component: Page404Layout,
  },
]

const router = createRouter({
  // history: createWebHistory(import.meta.env.VITE_APP_ROUTER_MODE_HISTORY === 'true' ? '/' : ''), // Use your way to determine history mode
  history: createWebHistory(import.meta.env.BASE_URL), // Standard way for Vite
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

// Update beforeEach guard
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()

  // Define public pages first
  const publicPages = [
    'login',
    'signup',
    'recover-password',
    'recover-password-email',
    'reset-password',
    'verify-2fa',
    'share-briefing',
  ]

  // Helper to run the rest of guards deterministically
  const runGuards = () => {
    // If going to non-auth page and not logged in - redirect to login
    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
    if (requiresAuth && !authStore.isAuthenticated) {
      console.log('[Router Guard] Auth required for', to.fullPath, 'redirecting to login')
      next({
        name: 'login',
        query: { redirect: to.fullPath },
      })
      return
    }

    // If logging in but already authenticated, redirect to organization dashboard
    if (publicPages.includes(to.name as string) && authStore.isAuthenticated) {
      // Skip this check for create-organization and public share briefings
      if (to.name !== 'create-organization' && to.name !== 'share-briefing' && !to.meta.publicShare) {
        console.log('[Router Guard] Already authenticated, redirecting from public page')
        if (authStore.organizationId) {
          console.log('[Router Guard] Redirecting to org dashboard:', authStore.organizationId)
          next({ name: 'admin', params: { orgId: authStore.organizationId } })
        } else {
          console.log('[Router Guard] No organization ID found, staying on current page')
          next() // Stay on current page, let the app handle it
        }
        return
      }
    }

    // Special handling for users without organization
    if (to.meta.requiresAuth && authStore.isAuthenticated && !authStore.hasOrganization) {
      // Skip org check for specific pages that don't require it
      if (to.name !== 'create-organization') {
        console.log('[Router Guard] User has no organization, redirecting to create one')
        next({ name: 'create-organization' }) // Create their organization before proceeding
        return
      }
    }

    // Organization context validation for organization-specific routes
    if (to.params.orgId && authStore.organizationId && to.params.orgId !== authStore.organizationId.toString()) {
      console.log("[Router Guard] Organization mismatch, redirecting to user's organization")
      next({ name: to.name as string, params: { ...to.params, orgId: authStore.organizationId } })
      return
    }

    // Admin access validation
    const requiresSuperuser = to.matched.some((record) => record.meta.requiresSuperuser)
    if (requiresSuperuser) {
      if (!authStore.isAuthenticated) {
        console.log('[Router Guard] Admin access requires authentication, redirecting to login')
        next({
          name: 'login',
          query: { redirect: to.fullPath },
        })
        return
      }

      if (!authStore.user?.is_superuser) {
        console.log('[Router Guard] Admin access requires superuser privileges, redirecting to dashboard')
        if (authStore.organizationId) {
          next({ name: 'admin', params: { orgId: authStore.organizationId } })
        } else {
          next({ name: 'dashboard' })
        }
        return
      }

      const adminSessionFlag = localStorage.getItem('admin_session_active') === 'true'
      const lastActivityRaw = localStorage.getItem('admin_last_activity')
      const sessionUser = localStorage.getItem('admin_session_user')
      const sessionTimeout = 30 * 60 * 1000 // 30 minutes

      const redirectToOrgDashboard = () => {
        if (authStore.organizationId) {
          next({ name: 'dashboard', params: { orgId: authStore.organizationId } })
        } else {
          next({ name: 'login' })
        }
      }

      if (!adminSessionFlag || !lastActivityRaw || !sessionUser) {
        console.log('[Router Guard] Admin session not active, redirecting to org dashboard')
        redirectToOrgDashboard()
        return
      }

      if (authStore.isInitialized && authStore.user?.id != null && sessionUser !== String(authStore.user.id)) {
        console.log('[Router Guard] Admin session user mismatch, redirecting to org dashboard')
        localStorage.removeItem('admin_session_active')
        localStorage.removeItem('admin_last_activity')
        localStorage.removeItem('admin_session_user')
        redirectToOrgDashboard()
        return
      }

      const timeSinceLastActivity = Date.now() - parseInt(lastActivityRaw, 10)
      if (timeSinceLastActivity > sessionTimeout) {
        console.log('[Router Guard] Admin session expired, redirecting to org dashboard')
        localStorage.removeItem('admin_session_active')
        localStorage.removeItem('admin_last_activity')
        localStorage.removeItem('admin_session_user')
        redirectToOrgDashboard()
        return
      }

      localStorage.setItem('admin_last_activity', Date.now().toString())
    }

    // Chain guards deterministically: feature flags first, then permissions
    // Use a wrapped next to detect if featureFlagGuard short-circuited
    let nextCalled = false
    const wrappedNext: typeof next = (...args: any[]) => {
      nextCalled = true
      // @ts-expect-error align with NavigationGuardNext
      return next(...args)
    }

    featureFlagGuard(to, from, wrappedNext)

    // If feature flag guard redirected or finished navigation, stop here
    if (nextCalled) {
      return
    }

    // Feature flag check did not short-circuit: continue to permission guard
    permissionGuard(to, from, next)
  }

  // Ensure auth store is initialized before proceeding
  if (!authStore.isInitialized) {
    let attempts = 0
    const maxAttempts = 50 // 5 seconds max wait
    const waitForAuth = () => {
      if (authStore.isInitialized) {
        // Now run the actual guards
        runGuards()
        return
      }
      attempts++
      if (attempts >= maxAttempts) {
        console.log('[Router Guard] Auth initialization timeout, redirecting to login')
        next({ name: 'login' })
        return
      }
      setTimeout(waitForAuth, 100)
    }
    waitForAuth()
    return
  }

  // Already initialized: run guards immediately
  runGuards()
})

export default router
